import { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  Loader2, Bot, Plus, Sparkles, Users, MessageSquare, Zap,
  ShieldCheck, BarChart3, PlayCircle, Send, Rocket, Archive,
  CheckCircle2, AlertTriangle, Trash2, X, Lock, Radio, RotateCcw,
  Info, FileText, ChevronRight, PhoneCall,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import {
  repBootstrap, repList, repCreate, repGet, repUpdate, repPublish,
  repArchive, repVersions, repRollback, repAudit, repConsents,
  repConsentAdd, repConsentWithdraw, repKnowledgeAvailable,
  repTestChat, repRunAction, repExecutions, repConversations,
  repConversationMessages, repIntelligence,
} from "@/lib/api";

const field = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500";
const btn = "inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition";
const btnPrimary = `${btn} bg-indigo-600 text-white hover:bg-indigo-500`;
const btnGhost = `${btn} bg-slate-100 text-slate-700 hover:bg-slate-200`;
const STATUS_COLOR = {
  draft: "bg-slate-100 text-slate-700",
  published: "bg-emerald-100 text-emerald-700",
  archived: "bg-slate-100 text-slate-500",
};
const HALLUC_COLOR = {
  high: "bg-emerald-100 text-emerald-700 border-emerald-200",
  medium: "bg-amber-100 text-amber-700 border-amber-200",
  low: "bg-rose-100 text-rose-700 border-rose-200",
  ungrounded: "bg-rose-200 text-rose-800 border-rose-300",
  policy_refusal: "bg-slate-200 text-slate-700 border-slate-300",
  unknown: "bg-slate-100 text-slate-600 border-slate-200",
};

// --- Tabs ------------------------------------------------------------------
const TABS = [
  { key: "personality", label: "Personality", icon: Users },
  { key: "knowledge",   label: "Knowledge",   icon: FileText },
  { key: "actions",     label: "Actions",     icon: Zap },
  { key: "channels",    label: "Channels",    icon: Radio },
  { key: "consent",     label: "Consent",     icon: ShieldCheck },
  { key: "analytics",   label: "Analytics",   icon: BarChart3 },
  { key: "test",        label: "Test",        icon: PlayCircle },
];

export default function RepresentativeStudio() {
  const { user } = useAuth();
  const { rid } = useParams();
  const navigate = useNavigate();
  const [boot, setBoot] = useState(null);
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("personality");
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    const b = await repBootstrap();
    setBoot(b);
    setList(b.representatives || []);
    setLoading(false);
    if (rid) {
      const r = (b.representatives || []).find((x) => x.id === rid);
      if (r) setSelected(r);
    } else if (b.representatives && b.representatives.length > 0 && !selected) {
      setSelected(b.representatives[0]);
    }
  }, [rid, selected]);

  useEffect(() => { if (user) reload().catch(() => setLoading(false)); }, [user, reload]);

  if (user === undefined) return <div className="min-h-screen grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (user === null) return <LoginGate />;
  if (loading) return <div className="min-h-screen flex flex-col"><Navbar /><div className="flex-1 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-indigo-500" /></div></div>;

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 pt-24 pb-16" data-testid="representative-studio">
        <div className="max-w-7xl mx-auto px-6">
          {/* Header */}
          <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
            <div>
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-indigo-700">
                <Bot className="w-3.5 h-3.5" /> AI Digital Representative
              </div>
              <h1 className="mt-2 text-3xl font-bold text-slate-900">Representative Studio</h1>
              <p className="mt-1 text-sm text-slate-500 italic">Expert Presence · Digital Front Desk · Live Presence</p>
            </div>
            <div className="flex gap-2">
              <Link to="/ai-representative" className={btnGhost} data-testid="learn-more-link"><Info className="w-4 h-4" /> Learn more</Link>
              <button onClick={() => setCreating(true)} className={btnPrimary} data-testid="new-rep-btn"><Plus className="w-4 h-4" /> New representative</button>
            </div>
          </div>

          <div className="grid lg:grid-cols-[280px_1fr] gap-6">
            {/* Left: rep list */}
            <aside className="space-y-2">
              {list.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-center" data-testid="empty-reps">
                  <Bot className="w-10 h-10 mx-auto text-slate-300" />
                  <div className="mt-3 text-sm font-semibold text-slate-900">No representatives yet</div>
                  <div className="mt-1 text-xs text-slate-500">Create your first digital representative in 5 minutes.</div>
                  <button onClick={() => setCreating(true)} className={`${btnPrimary} mt-4`}>Create representative</button>
                </div>
              ) : list.map((r) => (
                <button
                  key={r.id}
                  onClick={() => setSelected(r)}
                  data-testid={`rep-item-${r.id}`}
                  className={`w-full text-left rounded-xl border p-3 transition ${selected?.id === r.id ? "border-indigo-500 bg-indigo-50" : "border-slate-200 bg-white hover:border-slate-300"}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-semibold text-slate-900 truncate">{r.name}</div>
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${STATUS_COLOR[r.status]}`}>{r.status}</span>
                  </div>
                  <div className="mt-1 text-xs text-slate-500 truncate">{r.business_name}</div>
                  <div className="mt-2 flex items-center gap-1.5 text-[10px] text-slate-500">
                    <span className="rounded-full bg-slate-100 px-2 py-0.5">{r.package}</span>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5">v{r.version}</span>
                  </div>
                </button>
              ))}
            </aside>

            {/* Right: detail */}
            <section>
              {!selected ? (
                <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center">
                  <div className="text-sm text-slate-500">Select a representative to configure.</div>
                </div>
              ) : (
                <RepDetail
                  rep={selected}
                  boot={boot}
                  onReload={reload}
                  onSelect={setSelected}
                  tab={tab}
                  setTab={setTab}
                />
              )}
            </section>
          </div>
        </div>
      </main>
      {creating && (
        <CreateRepModal
          boot={boot}
          onClose={() => setCreating(false)}
          onCreated={(r) => { setCreating(false); reload(); setSelected(r); navigate(`/representative/${r.id}`); }}
        />
      )}
      <Footer />
    </div>
  );
}

function LoginGate() {
  return (
    <div className="min-h-screen flex flex-col"><Navbar />
      <div className="flex-1 grid place-items-center px-6">
        <div className="max-w-md text-center" data-testid="rep-login-gate">
          <Bot className="w-10 h-10 text-indigo-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Representative Studio</h1>
          <p className="text-slate-600 mb-6">Sign in to configure your AI Digital Representative.</p>
          <Link to="/login" className={btnPrimary}>Sign in</Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function CreateRepModal({ boot, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [templateKey, setTemplateKey] = useState("");
  const [pkg, setPkg] = useState("text");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    if (!name || !businessName) { setErr("Name and business name are required"); return; }
    setSaving(true); setErr("");
    try {
      const r = await repCreate({
        name, business_name: businessName,
        package: pkg, template_key: templateKey || null,
      });
      onCreated(r);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to create");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 grid place-items-center z-50 p-4" data-testid="create-rep-modal">
      <div className="bg-white rounded-2xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-slate-900">New representative</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Representative name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Ada" className={field + " mt-1"} data-testid="input-rep-name" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Business name</label>
            <input value={businessName} onChange={(e) => setBusinessName(e.target.value)} placeholder="e.g. ACME Dental" className={field + " mt-1"} data-testid="input-business-name" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Industry template (optional)</label>
            <select value={templateKey} onChange={(e) => setTemplateKey(e.target.value)} className={field + " mt-1"} data-testid="input-template">
              <option value="">— No template —</option>
              {boot?.industry_templates?.map((t) => (
                <option key={t.key} value={t.key}>{t.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Package</label>
            <div className="mt-1 grid grid-cols-3 gap-2">
              {[["text", "Text"], ["voice", "Voice"], ["twin", "Business Twin"]].map(([k, l]) => (
                <button key={k} onClick={() => setPkg(k)} data-testid={`pkg-${k}`}
                  className={`rounded-lg border px-3 py-2 text-xs font-semibold ${pkg === k ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-slate-200 bg-white text-slate-600"}`}>{l}</button>
              ))}
            </div>
          </div>
          {err && <div className="rounded-lg border border-rose-200 bg-rose-50 text-rose-700 text-xs p-3" data-testid="create-error">{err}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className={btnGhost}>Cancel</button>
            <button onClick={submit} disabled={saving} className={btnPrimary} data-testid="submit-create">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4" /> Create</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function RepDetail({ rep, boot, onReload, onSelect, tab, setTab }) {
  const [publishErr, setPublishErr] = useState("");
  const [busy, setBusy] = useState(false);

  const doPublish = async () => {
    setPublishErr(""); setBusy(true);
    try {
      const r = await repPublish(rep.id);
      onReload();
      onSelect(r);
    } catch (e) {
      setPublishErr(e?.response?.data?.detail || "Publish failed");
    } finally { setBusy(false); }
  };

  const doArchive = async () => {
    if (!window.confirm("Archive this representative? It will stop responding.")) return;
    await repArchive(rep.id); onReload();
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="rep-detail">
      {/* Header */}
      <div className="p-6 border-b border-slate-100">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold text-slate-900">{rep.name}</h2>
              <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${STATUS_COLOR[rep.status]}`}>{rep.status}</span>
              <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">v{rep.version}</span>
            </div>
            <div className="mt-1 text-sm text-slate-500">
              {rep.role} · {rep.business_name} · Package {rep.package}
            </div>
          </div>
          <div className="flex gap-2">
            {rep.status !== "archived" && (
              <button onClick={doPublish} disabled={busy || rep.status === "published"} className={btnPrimary} data-testid="publish-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
                {rep.status === "published" ? "Republish" : "Publish"}
              </button>
            )}
            {rep.status !== "archived" && (
              <button onClick={doArchive} className={btnGhost} data-testid="archive-btn"><Archive className="w-4 h-4" /> Archive</button>
            )}
          </div>
        </div>
        {publishErr && (
          <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 text-rose-700 text-xs p-3 flex items-start gap-2" data-testid="publish-error">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /><div>{publishErr}</div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-100 px-4 flex flex-wrap gap-1 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
            className={`flex items-center gap-1.5 px-3 py-3 text-xs font-semibold border-b-2 transition ${tab === t.key ? "border-indigo-600 text-indigo-600" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="p-6">
        {tab === "personality" && <TabPersonality rep={rep} onReload={onReload} onSelect={onSelect} />}
        {tab === "knowledge"   && <TabKnowledge   rep={rep} onReload={onReload} onSelect={onSelect} />}
        {tab === "actions"     && <TabActions     rep={rep} boot={boot} onReload={onReload} onSelect={onSelect} />}
        {tab === "channels"    && <TabChannels    rep={rep} onReload={onReload} onSelect={onSelect} />}
        {tab === "consent"     && <TabConsent     rep={rep} boot={boot} onReload={onReload} />}
        {tab === "analytics"   && <TabAnalytics   rep={rep} />}
        {tab === "test"        && <TabTest        rep={rep} />}
      </div>
    </div>
  );
}

// ---------- Personality tab ---------- //
function TabPersonality({ rep, onReload, onSelect }) {
  const p = rep.personality || {};
  const [form, setForm] = useState({
    tone: p.tone || "friendly",
    greeting: p.greeting || "",
    intro: p.intro || "",
    owner_bio: p.owner_bio || "",
    vocabulary_prefer: (p.vocabulary_prefer || []).join(", "),
    vocabulary_avoid: (p.vocabulary_avoid || []).join(", "),
    brand_values: (p.brand_values || []).join(", "),
    expertise: (p.expertise || []).join(", "),
    allowed_topics: (p.allowed_topics || []).join(", "),
    refused_topics: (p.refused_topics || []).join(", "),
    prohibited_claims: (p.prohibited_claims || []).join(", "),
    preferred_ctas: (p.preferred_ctas || []).join(", "),
    emergency_instructions: p.emergency_instructions || "",
    after_hours_message: p.after_hours_message || "",
    handoff_rules: p.handoff_rules || "",
  });
  const [verified, setVerified] = useState(rep.verified_authority);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    const listify = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);
    const patch = {
      verified_authority: verified,
      personality: {
        tone: form.tone,
        greeting: form.greeting,
        intro: form.intro,
        owner_bio: form.owner_bio,
        vocabulary_prefer: listify(form.vocabulary_prefer),
        vocabulary_avoid: listify(form.vocabulary_avoid),
        brand_values: listify(form.brand_values),
        expertise: listify(form.expertise),
        allowed_topics: listify(form.allowed_topics),
        refused_topics: listify(form.refused_topics),
        prohibited_claims: listify(form.prohibited_claims),
        preferred_ctas: listify(form.preferred_ctas),
        emergency_instructions: form.emergency_instructions,
        after_hours_message: form.after_hours_message,
        handoff_rules: form.handoff_rules,
      },
    };
    try {
      const r = await repUpdate(rep.id, patch);
      onSelect(r); onReload();
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="personality-tab" className="space-y-5">
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" checked={verified} onChange={(e) => setVerified(e.target.checked)} className="mt-1" data-testid="verified-authority" />
          <div>
            <div className="text-sm font-semibold text-amber-900">I confirm I have authority to create a representative for this business.</div>
            <div className="mt-1 text-xs text-amber-800">Required before publishing. Impersonating another business or person is prohibited.</div>
          </div>
        </label>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Tone">
          <select value={form.tone} onChange={(e) => setForm({...form, tone: e.target.value})} className={field} data-testid="p-tone">
            {["friendly", "formal", "reassuring", "educational", "sales", "concise"].map((t) => <option key={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Greeting">
          <input value={form.greeting} onChange={(e) => setForm({...form, greeting: e.target.value})} className={field} data-testid="p-greeting" />
        </Field>
        <FieldFull label="Introduction (short 'about me')">
          <textarea rows={2} value={form.intro} onChange={(e) => setForm({...form, intro: e.target.value})} className={field} data-testid="p-intro" />
        </FieldFull>
        <FieldFull label="Owner or staff biography">
          <textarea rows={3} value={form.owner_bio} onChange={(e) => setForm({...form, owner_bio: e.target.value})} className={field} data-testid="p-owner-bio" />
        </FieldFull>
        <Field label="Brand values (comma-separated)">
          <input value={form.brand_values} onChange={(e) => setForm({...form, brand_values: e.target.value})} className={field} data-testid="p-brand-values" />
        </Field>
        <Field label="Areas of expertise (comma-separated)">
          <input value={form.expertise} onChange={(e) => setForm({...form, expertise: e.target.value})} className={field} data-testid="p-expertise" />
        </Field>
        <Field label="Prefer these words">
          <input value={form.vocabulary_prefer} onChange={(e) => setForm({...form, vocabulary_prefer: e.target.value})} className={field} data-testid="p-vocab-prefer" />
        </Field>
        <Field label="Avoid these words">
          <input value={form.vocabulary_avoid} onChange={(e) => setForm({...form, vocabulary_avoid: e.target.value})} className={field} data-testid="p-vocab-avoid" />
        </Field>
        <Field label="Allowed topics">
          <input value={form.allowed_topics} onChange={(e) => setForm({...form, allowed_topics: e.target.value})} className={field} data-testid="p-allowed" />
        </Field>
        <Field label="Refused topics (auto-handoff)">
          <input value={form.refused_topics} onChange={(e) => setForm({...form, refused_topics: e.target.value})} className={field} data-testid="p-refused" />
        </Field>
        <Field label="Prohibited claims">
          <input value={form.prohibited_claims} onChange={(e) => setForm({...form, prohibited_claims: e.target.value})} className={field} data-testid="p-prohibited" />
        </Field>
        <Field label="Preferred calls-to-action">
          <input value={form.preferred_ctas} onChange={(e) => setForm({...form, preferred_ctas: e.target.value})} className={field} data-testid="p-ctas" />
        </Field>
        <FieldFull label="After-hours message">
          <textarea rows={2} value={form.after_hours_message} onChange={(e) => setForm({...form, after_hours_message: e.target.value})} className={field} data-testid="p-after-hours" />
        </FieldFull>
        <FieldFull label="Emergency instructions">
          <textarea rows={2} value={form.emergency_instructions} onChange={(e) => setForm({...form, emergency_instructions: e.target.value})} className={field} data-testid="p-emergency" />
        </FieldFull>
        <FieldFull label="Handoff rules">
          <textarea rows={2} value={form.handoff_rules} onChange={(e) => setForm({...form, handoff_rules: e.target.value})} className={field} data-testid="p-handoff-rules" />
        </FieldFull>
      </div>

      <div className="flex items-center justify-end gap-2">
        {saved && <span className="text-xs text-emerald-700 font-semibold flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Saved</span>}
        <button onClick={save} disabled={saving} className={btnPrimary} data-testid="save-personality">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Save
        </button>
      </div>
    </div>
  );
}

// ---------- Knowledge tab ---------- //
function TabKnowledge({ rep, onReload, onSelect }) {
  const [avail, setAvail] = useState({ documents: [], tags: [] });
  const [scope, setScope] = useState(rep.knowledge_scope || []);
  const [saving, setSaving] = useState(false);

  useEffect(() => { repKnowledgeAvailable(rep.id).then(setAvail).catch(() => {}); }, [rep.id]);

  const toggleTag = (t) => setScope((s) => s.includes(t) ? s.filter((x) => x !== t) : [...s, t]);

  const save = async () => {
    setSaving(true);
    try { const r = await repUpdate(rep.id, { knowledge_scope: scope }); onSelect(r); onReload(); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="knowledge-tab">
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
        <div className="flex items-start gap-2">
          <Info className="w-4 h-4 text-indigo-600 mt-0.5" />
          <div className="text-xs text-slate-700">
            Your representative only answers from <strong>approved</strong> knowledge (managed in <Link to="/wtc?tab=knowledge" className="text-indigo-700 underline">WebsiteTech Center · Knowledge</Link>).
            Leave scope empty to allow all approved knowledge. Restrict by tag for a more focused representative.
          </div>
        </div>
      </div>

      <div>
        <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">Available tags ({avail.tags.length})</div>
        <div className="flex flex-wrap gap-2">
          {avail.tags.length === 0 && <div className="text-xs text-slate-500">No tagged approved knowledge yet.</div>}
          {avail.tags.map((t) => (
            <button key={t} onClick={() => toggleTag(t)} data-testid={`tag-${t}`}
              className={`rounded-full px-3 py-1 text-xs font-semibold border ${scope.includes(t) ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-700 border-slate-300"}`}>
              {t}
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">Approved documents ({avail.documents.length})</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-80 overflow-y-auto">
          {avail.documents.map((d) => (
            <div key={d.id} className="rounded-lg border border-slate-200 bg-white p-3 text-xs">
              <div className="font-semibold text-slate-900 truncate">{d.title}</div>
              <div className="mt-1 text-slate-500 flex flex-wrap gap-1">
                <span className="rounded-full bg-slate-100 px-2 py-0.5">{d.kind}</span>
                {(d.tags || []).map((t) => <span key={t} className="rounded-full bg-indigo-50 text-indigo-700 px-2 py-0.5">{t}</span>)}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} className={btnPrimary} data-testid="save-knowledge">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Save scope
        </button>
      </div>
    </div>
  );
}

// ---------- Actions tab ---------- //
function TabActions({ rep, boot, onReload, onSelect }) {
  const [actions, setActions] = useState(rep.actions || {});
  const [saving, setSaving] = useState(false);
  const catalog = boot?.action_catalog || [];

  useEffect(() => { setActions(rep.actions || {}); }, [rep]);

  const setField = (key, field, value) => setActions((a) => ({ ...a, [key]: { ...(a[key] || {}), [field]: value } }));

  const save = async () => {
    setSaving(true);
    try { const r = await repUpdate(rep.id, { actions }); onSelect(r); onReload(); }
    finally { setSaving(false); }
  };

  const byCategory = catalog.reduce((acc, a) => {
    (acc[a.category] = acc[a.category] || []).push(a); return acc;
  }, {});

  return (
    <div className="space-y-5" data-testid="actions-tab">
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
        <div className="flex items-start gap-2 text-xs text-amber-900">
          <Lock className="w-4 h-4 mt-0.5" />
          <div>Your representative can only perform actions <strong>you turn on</strong> below. High-risk actions require a human approval on the AI Approvals queue.</div>
        </div>
      </div>

      {Object.entries(byCategory).map(([cat, items]) => (
        <div key={cat}>
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">{cat}</div>
          <div className="grid md:grid-cols-2 gap-3">
            {items.map((a) => {
              const p = actions[a.key] || {};
              return (
                <div key={a.key} className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`action-${a.key}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-slate-900">{a.label}</div>
                      <div className="mt-0.5 text-[11px] text-slate-500">{a.key}{a.high_risk && <span className="ml-2 text-rose-600 font-semibold">HIGH-RISK</span>}</div>
                    </div>
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={!!p.enabled} onChange={(e) => setField(a.key, "enabled", e.target.checked)} data-testid={`action-${a.key}-enabled`} />
                      <span className="text-xs text-slate-700">{p.enabled ? "On" : "Off"}</span>
                    </label>
                  </div>
                  {p.enabled && (
                    <div className="mt-3 flex flex-wrap gap-3 text-xs text-slate-600">
                      <label className="inline-flex items-center gap-1.5">
                        <input type="checkbox" checked={!!p.requires_confirmation} onChange={(e) => setField(a.key, "requires_confirmation", e.target.checked)} />
                        Confirm before running
                      </label>
                      <label className="inline-flex items-center gap-1.5">
                        <input type="checkbox" checked={!!p.requires_human_approval} onChange={(e) => setField(a.key, "requires_human_approval", e.target.checked)} />
                        Require human approval
                      </label>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} className={btnPrimary} data-testid="save-actions">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Save
        </button>
      </div>
    </div>
  );
}

// ---------- Channels tab ---------- //
function TabChannels({ rep, onReload, onSelect }) {
  const [channels, setChannels] = useState(rep.channels || ["web"]);
  const [languages, setLanguages] = useState(rep.languages || ["en"]);
  const [saving, setSaving] = useState(false);
  const CHANNELS = ["web", "voice", "phone", "sms", "whatsapp", "email"];
  const LANGS = ["en", "fr", "es", "de", "pt", "hi", "ar", "ja", "zh"];

  const toggle = (arr, setter, v) => setter(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const save = async () => {
    setSaving(true);
    try { const r = await repUpdate(rep.id, { channels, languages }); onSelect(r); onReload(); }
    finally { setSaving(false); }
  };

  const publicUrl = `${process.env.REACT_APP_BACKEND_URL}/api/rep/${rep.id}/widget.js`;
  const snippet = `<script src="${publicUrl}" async></script>`;

  return (
    <div className="space-y-6" data-testid="channels-tab">
      <div>
        <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">Enabled channels</div>
        <div className="flex flex-wrap gap-2">
          {CHANNELS.map((c) => (
            <button key={c} onClick={() => toggle(channels, setChannels, c)} data-testid={`ch-${c}`}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold border ${channels.includes(c) ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-700 border-slate-300"}`}>
              {c}
            </button>
          ))}
        </div>
        <div className="mt-2 text-[11px] text-slate-500">Voice / phone / WhatsApp require credentials — see integrations.</div>
      </div>

      <div>
        <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">Supported languages</div>
        <div className="flex flex-wrap gap-2">
          {LANGS.map((l) => (
            <button key={l} onClick={() => toggle(languages, setLanguages, l)} data-testid={`lang-${l}`}
              className={`rounded-full px-3 py-1 text-xs font-semibold border ${languages.includes(l) ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300"}`}>
              {l}
            </button>
          ))}
        </div>
        <div className="mt-2 text-[11px] text-slate-500">Auto-detects visitor language; falls back to first supported.</div>
      </div>

      {rep.status === "published" && (
        <div>
          <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">Embed on your website</div>
          <div className="rounded-lg border border-slate-200 bg-slate-900 text-slate-100 p-3 text-xs font-mono overflow-x-auto" data-testid="embed-snippet">
            {snippet}
          </div>
          <div className="mt-2 flex items-center gap-2">
            <button onClick={() => navigator.clipboard.writeText(snippet)} className={btnGhost} data-testid="copy-snippet"><Send className="w-3.5 h-3.5" /> Copy</button>
            <span className="text-[11px] text-slate-500">Paste before &lt;/body&gt; on any page.</span>
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} className={btnPrimary} data-testid="save-channels">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Save
        </button>
      </div>
    </div>
  );
}

// ---------- Consent tab ---------- //
function TabConsent({ rep, boot, onReload }) {
  const [items, setItems] = useState([]);
  const [disclosures, setDisclosures] = useState(rep.disclosures || {});
  const [newC, setNewC] = useState({ kind: "voice_clone", person_name: "", scope: "", evidence: "" });
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const reload = useCallback(() => repConsents(rep.id).then((d) => setItems(d.items || [])), [rep.id]);
  useEffect(() => { reload(); }, [reload]);

  const add = async () => {
    if (!newC.person_name || !newC.evidence) return;
    setAdding(true);
    try {
      await repConsentAdd(rep.id, newC);
      setNewC({ kind: "voice_clone", person_name: "", scope: "", evidence: "" });
      reload();
    } finally { setAdding(false); }
  };

  const withdraw = async (cid) => {
    if (!window.confirm("Withdraw this consent? Any voice/twin package will auto-unpublish.")) return;
    await repConsentWithdraw(rep.id, cid);
    reload(); onReload();
  };

  const saveDisclosures = async () => {
    setSaving(true);
    try { await repUpdate(rep.id, { disclosures }); onReload(); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="consent-tab">
      <div>
        <div className="text-sm font-bold text-slate-900 mb-3">Disclosures</div>
        <div className="grid md:grid-cols-2 gap-4">
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={!!disclosures.ai_disclosure_enabled} onChange={(e) => setDisclosures({...disclosures, ai_disclosure_enabled: e.target.checked})} data-testid="ai-disclosure-toggle" className="mt-1" />
            <div>
              <div className="text-sm font-semibold text-slate-900">Show AI disclosure to visitors</div>
              <div className="text-xs text-slate-500">A visible message that visitors are chatting with AI.</div>
            </div>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={!!disclosures.recording_consent_required} onChange={(e) => setDisclosures({...disclosures, recording_consent_required: e.target.checked})} data-testid="recording-consent-toggle" className="mt-1" />
            <div>
              <div className="text-sm font-semibold text-slate-900">Require recording consent</div>
              <div className="text-xs text-slate-500">Prompt visitors before recording/transcription.</div>
            </div>
          </label>
          <FieldFull label="AI disclosure text">
            <textarea rows={2} value={disclosures.ai_disclosure_text || ""} onChange={(e) => setDisclosures({...disclosures, ai_disclosure_text: e.target.value})} className={field} data-testid="ai-disclosure-text" />
          </FieldFull>
          <FieldFull label="Recording consent text">
            <textarea rows={2} value={disclosures.recording_consent_text || ""} onChange={(e) => setDisclosures({...disclosures, recording_consent_text: e.target.value})} className={field} data-testid="recording-consent-text" />
          </FieldFull>
          <Field label="Retention (days)">
            <input type="number" value={disclosures.retention_days || 365} onChange={(e) => setDisclosures({...disclosures, retention_days: Number(e.target.value)})} className={field} data-testid="retention-days" />
          </Field>
          <Field label="Restricted-topics policy">
            <select value={disclosures.restricted_topics_policy || "refuse_and_handoff"} onChange={(e) => setDisclosures({...disclosures, restricted_topics_policy: e.target.value})} className={field}>
              <option value="refuse_and_handoff">Refuse + disclaimer + handoff (recommended)</option>
              <option value="educational_only">Educational info only</option>
            </select>
          </Field>
        </div>
        <div className="mt-4 flex justify-end">
          <button onClick={saveDisclosures} disabled={saving} className={btnPrimary} data-testid="save-disclosures">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Save disclosures
          </button>
        </div>
      </div>

      <div className="border-t pt-6">
        <div className="text-sm font-bold text-slate-900 mb-3">Consent records ({items.length})</div>
        <p className="text-xs text-slate-500 mb-4">Voice or likeness cloning requires a stored, revocable consent. Withdrawal immediately unpublishes voice/twin packages.</p>

        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 mb-4 grid md:grid-cols-2 gap-3">
          <Field label="Kind">
            <select value={newC.kind} onChange={(e) => setNewC({...newC, kind: e.target.value})} className={field} data-testid="new-consent-kind">
              {(boot?.consent_kinds || []).map((k) => <option key={k}>{k}</option>)}
            </select>
          </Field>
          <Field label="Person name">
            <input value={newC.person_name} onChange={(e) => setNewC({...newC, person_name: e.target.value})} className={field} data-testid="new-consent-person" />
          </Field>
          <FieldFull label="Scope">
            <input value={newC.scope} onChange={(e) => setNewC({...newC, scope: e.target.value})} className={field} placeholder="e.g. voice clone for hotel reception" data-testid="new-consent-scope" />
          </FieldFull>
          <FieldFull label="Evidence (signed doc reference / date / text)">
            <textarea rows={2} value={newC.evidence} onChange={(e) => setNewC({...newC, evidence: e.target.value})} className={field} data-testid="new-consent-evidence" />
          </FieldFull>
          <div className="md:col-span-2 flex justify-end">
            <button onClick={add} disabled={adding || !newC.person_name || !newC.evidence} className={btnPrimary} data-testid="add-consent-btn">
              {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Record consent
            </button>
          </div>
        </div>

        <div className="space-y-2">
          {items.length === 0 && <div className="text-xs text-slate-500">No consent records yet.</div>}
          {items.map((c) => (
            <div key={c.id} className="rounded-lg border border-slate-200 bg-white p-3 flex items-start justify-between gap-3" data-testid={`consent-${c.id}`}>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-900">{c.person_name} <span className="text-[10px] font-bold uppercase text-slate-500 ml-1">{c.kind}</span></div>
                <div className="mt-0.5 text-xs text-slate-600 truncate">{c.scope}</div>
                <div className="mt-1 text-[11px] text-slate-400">Granted {c.granted_at?.slice(0, 10)} · Evidence: {(c.evidence || "").slice(0, 80)}</div>
              </div>
              {c.status === "active" ? (
                <button onClick={() => withdraw(c.id)} className="text-xs font-semibold text-rose-600 hover:text-rose-700 inline-flex items-center gap-1" data-testid={`withdraw-${c.id}`}>
                  <Trash2 className="w-3.5 h-3.5" /> Withdraw
                </button>
              ) : (
                <span className="text-[10px] font-bold uppercase text-slate-500">Withdrawn</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------- Analytics tab ---------- //
function TabAnalytics({ rep }) {
  const [intel, setIntel] = useState(null);
  const [convs, setConvs] = useState([]);
  useEffect(() => {
    repIntelligence(rep.id).then(setIntel).catch(() => {});
    repConversations(rep.id).then((d) => setConvs(d.items || [])).catch(() => {});
  }, [rep.id]);

  if (!intel) return <div className="grid place-items-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  const T = intel.totals || {};
  return (
    <div className="space-y-6" data-testid="analytics-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Conversations" value={T.conversations} />
        <Tile label="Messages" value={T.messages} />
        <Tile label="Handoffs" value={T.handoffs} />
        <Tile label="Unanswered" value={T.unanswered} />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <MiniBars title="Top intents" items={intel.intents} />
        <MiniBars title="Sentiment" items={intel.sentiments} />
        <MiniBars title="Languages" items={intel.languages} />
        <MiniBars title="Actions performed" items={intel.actions} />
      </div>

      <div>
        <div className="text-sm font-bold text-slate-900 mb-2">Low-grounding replies (review)</div>
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {(intel.recent_low_confidence || []).length === 0 && <div className="text-xs text-slate-500">All recent replies were well-grounded ✓</div>}
          {(intel.recent_low_confidence || []).map((m) => (
            <div key={m.id} className="rounded-lg border border-slate-200 bg-white p-3 text-xs" data-testid={`low-conf-${m.id}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className={`px-2 py-0.5 rounded-full font-bold uppercase text-[10px] border ${HALLUC_COLOR[m.hallucination?.level] || HALLUC_COLOR.unknown}`}>{m.hallucination?.level || "unknown"} · {m.hallucination?.score ?? 0}</span>
                <span className="text-slate-400">{m.at?.slice(0, 16)}</span>
              </div>
              <div className="text-slate-700 line-clamp-3">{m.text}</div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="text-sm font-bold text-slate-900 mb-2">Recent conversations</div>
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {convs.slice(0, 10).map((c) => (
            <div key={c.id} className="text-xs text-slate-600 flex items-center justify-between gap-2 rounded border border-slate-200 bg-white px-3 py-2">
              <span className="truncate">{c.session_id}</span>
              <span className="text-slate-400">{c.message_count} msgs · {c.language} · {c.last_at?.slice(0, 16)}</span>
            </div>
          ))}
          {convs.length === 0 && <div className="text-xs text-slate-500">No conversations yet — test your representative first.</div>}
        </div>
      </div>
    </div>
  );
}

function Tile({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-bold text-slate-900" data-testid={`tile-${label.toLowerCase()}`}>{value ?? 0}</div>
    </div>
  );
}

function MiniBars({ title, items }) {
  const total = (items || []).reduce((a, b) => a + (b.count || 0), 0) || 1;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">{title}</div>
      <div className="space-y-1.5">
        {(items || []).slice(0, 6).map((it) => (
          <div key={it.label} className="text-xs">
            <div className="flex items-center justify-between">
              <span className="text-slate-700">{it.label}</span>
              <span className="text-slate-500">{it.count}</span>
            </div>
            <div className="mt-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full bg-indigo-500" style={{width: `${(it.count/total)*100}%`}} />
            </div>
          </div>
        ))}
        {(items || []).length === 0 && <div className="text-xs text-slate-400">No data yet</div>}
      </div>
    </div>
  );
}

// ---------- Test tab ---------- //
function TabTest({ rep }) {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sid] = useState(`preview-${Math.random().toString(36).slice(2, 10)}`);

  const send = async () => {
    if (!input.trim()) return;
    const my = { role: "visitor", text: input, at: new Date().toISOString() };
    setMsgs((m) => [...m, my]);
    setInput(""); setSending(true);
    try {
      const r = await repTestChat(rep.id, { message: my.text, session_id: sid });
      setMsgs((m) => [...m, {
        role: "assistant",
        text: r.reply,
        citations: r.citations,
        hallucination: r.hallucination,
        intent: r.intent,
        restricted: r.restricted,
        language: r.language,
      }]);
    } catch (e) {
      setMsgs((m) => [...m, { role: "assistant", text: `Error: ${e?.response?.data?.detail || "failed"}` }]);
    } finally { setSending(false); }
  };

  return (
    <div className="space-y-3" data-testid="test-tab">
      <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-xs text-indigo-900">
        <div className="font-semibold mb-1">Preview mode — talks even in draft.</div>
        Test as a visitor. Every reply shows grounding score, citations, intent, and detected language.
      </div>

      <div className="rounded-xl border border-slate-200 bg-slate-50 h-96 overflow-y-auto p-4 space-y-3" data-testid="test-transcript">
        {msgs.length === 0 && <div className="text-xs text-slate-500 text-center py-10">Say hi to your representative to see how it responds.</div>}
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.role === "visitor" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] rounded-lg p-3 ${m.role === "visitor" ? "bg-indigo-600 text-white" : "bg-white border border-slate-200"}`}>
              <div className="text-sm whitespace-pre-wrap">{m.text}</div>
              {m.role === "assistant" && m.hallucination && (
                <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[10px]">
                  <span className={`px-2 py-0.5 rounded-full font-bold uppercase border ${HALLUC_COLOR[m.hallucination.level] || HALLUC_COLOR.unknown}`} data-testid="halluc-badge">Grounding · {m.hallucination.level} · {m.hallucination.score}</span>
                  {m.language && m.language !== "en" && <span className="rounded-full bg-slate-100 text-slate-700 px-2 py-0.5">lang: {m.language}</span>}
                  {m.restricted && <span className="rounded-full bg-rose-100 text-rose-700 px-2 py-0.5">restricted: {m.restricted}</span>}
                  {m.intent && <span className="rounded-full bg-slate-100 text-slate-700 px-2 py-0.5">intent: {m.intent.primary}</span>}
                </div>
              )}
              {m.role === "assistant" && m.citations && m.citations.length > 0 && (
                <div className="mt-2 text-[10px] text-slate-500">Sources: {m.citations.map((c) => c.title).join(" · ")}</div>
              )}
            </div>
          </div>
        ))}
        {sending && <div className="flex items-center gap-2 text-xs text-slate-500"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Thinking…</div>}
      </div>

      <div className="flex gap-2">
        <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a visitor message…" className={field} data-testid="test-input" />
        <button onClick={send} disabled={sending || !input.trim()} className={btnPrimary} data-testid="test-send">Send</button>
      </div>
    </div>
  );
}

// -------- Helpers --------
function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function FieldFull({ label, children }) {
  return (
    <div className="md:col-span-2">
      <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
